import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import WaitlistDialog from '../components/WaitlistDialog';
import { EARLY_ACCESS } from '../config';
import './Login.css';

/* One card, several views. Signing in, signing up, confirming an emailed code
 * and resetting a forgotten password are all the same card with different
 * contents — a player moving between them should feel the card change its mind,
 * not the page navigate.
 *
 * Copy rule followed throughout: the button says what will happen, and the
 * confirmation uses the same word back. "Send reset code" → "Code sent".
 */
const VIEWS = {
  signIn: {
    heading: 'Welcome back',
    blurb: 'Sign in to take your seat',
  },
  signUp: {
    heading: 'Take a seat',
    blurb: 'Six players, 108 tiles, one pot',
  },
  confirm: {
    heading: 'Check your email',
    blurb: 'We sent you a six-digit code',
  },
  forgot: {
    heading: 'Reset your password',
    blurb: 'We will email you a code',
  },
  reset: {
    heading: 'Choose a new password',
    blurb: 'Enter the code we emailed you',
  },
  newPassword: {
    heading: 'Choose your password',
    blurb: 'Your account was created with a temporary one',
  },
};

// Mirrors the Cognito pool's policy and the server's own check, so the player
// is told before a round trip rather than after one.
function passwordProblem(password) {
  if (password.length < 10) return 'At least 10 characters';
  if (!/[a-z]/.test(password)) return 'Add a lowercase letter';
  if (!/[A-Z]/.test(password)) return 'Add an uppercase letter';
  if (!/[0-9]/.test(password)) return 'Add a number';
  return null;
}

export default function Login() {
  const {
    login, signUp, confirmSignUp, resendCode,
    forgotPassword, resetPassword, completeNewPassword,
  } = useAuth();
  const navigate = useNavigate();

  const [view, setView] = useState('signIn');
  const [waitlistOpen, setWaitlistOpen] = useState(false);

  const [identifier, setIdentifier] = useState('');
  const [email,      setEmail]      = useState('');
  const [name,       setName]       = useState('');
  const [password,   setPassword]   = useState('');
  const [code,       setCode]       = useState('');
  const [challenge,  setChallenge]  = useState(null);

  const [error,   setError]   = useState('');
  const [notice,  setNotice]  = useState('');
  const [loading, setLoading] = useState(false);

  /* Clears whatever the last view left behind. Without this a stale error sits
   * above an unrelated form, and a password typed on the sign-up view would
   * survive into the reset view. */
  const go = (next, { keepEmail = false } = {}) => {
    setView(next);
    setError('');
    setNotice('');
    setPassword('');
    setCode('');
    if (!keepEmail) { setEmail(''); setName(''); }
  };

  // Every form shares this: one in-flight request, errors surfaced, never both
  // an error and a notice at once.
  const submit = (handler) => async (e) => {
    e.preventDefault();
    setError('');
    setNotice('');
    setLoading(true);
    try {
      await handler();
    } catch (err) {
      setError(err.message);
      /* An unconfirmed account is not really a failure — the player has an
       * account and simply never entered their code. Send them to the step
       * they are actually on. */
      if (err.code === 'unconfirmed') {
        setEmail(identifier.includes('@') ? identifier : email);
        setView('confirm');
        setError('');
        setNotice('Confirm your email address to finish signing in.');
      }
      /* An admin-created account signing in with its temporary password. The
       * session is opaque and short-lived, so go straight there. */
      if (err.code === 'new_password_required' && err.session) {
        setChallenge(err.session);
        setEmail(identifier.includes('@') ? identifier : email);
        setView('newPassword');
        setError('');
      }
    } finally {
      setLoading(false);
    }
  };

  const onSignIn = submit(async () => {
    await login(identifier, password);
    navigate('/dashboard');
  });

  const onSignUp = submit(async () => {
    const problem = passwordProblem(password);
    if (problem) throw Object.assign(new Error(problem), { code: 'weak_password' });

    await signUp(email, password, name);
    setView('confirm');
    setNotice(`We sent a code to ${email}.`);
    setPassword('');
  });

  const onConfirm = submit(async () => {
    await confirmSignUp(email, code);
    go('signIn');
    setIdentifier(email);
    setNotice('Account confirmed. Sign in to take your seat.');
  });

  const onResend = async () => {
    setError('');
    try {
      await resendCode(email);
      setNotice(`We sent another code to ${email}.`);
      setCode('');
    } catch (err) {
      setError(err.message);
    }
  };

  const onForgot = submit(async () => {
    await forgotPassword(email);
    setView('reset');
    // Deliberately the same whether or not the address is registered — the
    // server reports success either way, and so must this.
    setNotice(`If ${email} has an account, a code is on its way.`);
  });

  const onReset = submit(async () => {
    const problem = passwordProblem(password);
    if (problem) throw Object.assign(new Error(problem), { code: 'weak_password' });

    await resetPassword(email, code, password);
    go('signIn');
    setIdentifier(email);
    setNotice('Password changed. Sign in with your new one.');
  });

  const onNewPassword = submit(async () => {
    const problem = passwordProblem(password);
    if (problem) throw Object.assign(new Error(problem), { code: 'weak_password' });

    await completeNewPassword(email, challenge, password);
    navigate('/dashboard');
  });

  const { heading, blurb } = VIEWS[view];

  return (
    <div className="login-page">
      <div className="login-bg" aria-hidden="true">
        {['🀄','🀅','🀆','🀇','🀈','🀙','🀚','🀀','🀁','🀂'].map((t, i) => (
          <span key={i} className="login-tile" style={{ '--d': `${i * 0.4}s`, '--r': `${i * 23 % 40 - 20}deg` }}>{t}</span>
        ))}
      </div>

      <Link to="/" className="login-logo">🀄 Triad-Ha</Link>

      <WaitlistDialog
        open={waitlistOpen}
        source="login"
        onClose={() => setWaitlistOpen(false)}
      />

      <div className="login-card">
        <div className="login-card-header">
          <h1>{heading}</h1>
          <p>{blurb}</p>
        </div>

        {error  && <div className="login-error" role="alert">{error}</div>}
        {notice && <div className="login-notice">{notice}</div>}

        {view === 'signIn' && (
          <>
            <form className="login-form" onSubmit={onSignIn}>
              <div className="field">
                <label htmlFor="identifier">Email or username</label>
                <input
                  id="identifier" type="text" autoComplete="username"
                  placeholder="you@example.com"
                  value={identifier} onChange={(e) => setIdentifier(e.target.value)}
                  required
                />
              </div>

              <div className="field">
                <label htmlFor="password">Password</label>
                <input
                  id="password" type="password" autoComplete="current-password"
                  placeholder="••••••••"
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>

              <button type="submit" className="login-submit" disabled={loading}>
                {loading ? 'Signing in…' : 'Sign in'}
              </button>
            </form>

            <p className="login-footer-text">
              <button type="button" className="login-link" onClick={() => go('forgot')}>
                Forgotten your password?
              </button>
            </p>
            <p className="login-footer-text">
              {EARLY_ACCESS ? (
                <>
                  No account?{' '}
                  <button type="button" className="login-link" onClick={() => setWaitlistOpen(true)}>
                    Join the waitlist
                  </button>
                </>
              ) : (
                <>
                  No account?{' '}
                  <button type="button" className="login-link" onClick={() => go('signUp')}>
                    Create one
                  </button>
                </>
              )}
            </p>
          </>
        )}

        {view === 'signUp' && (
          <>
            <form className="login-form" onSubmit={onSignUp}>
              <div className="field">
                <label htmlFor="name">Display name</label>
                <input
                  id="name" type="text" autoComplete="nickname"
                  placeholder="Shown at the table"
                  value={name} onChange={(e) => setName(e.target.value)}
                  required minLength={2} maxLength={24}
                />
              </div>

              <div className="field">
                <label htmlFor="email">Email</label>
                <input
                  id="email" type="email" autoComplete="email"
                  placeholder="you@example.com"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              <div className="field">
                <label htmlFor="new-password">Password</label>
                <input
                  id="new-password" type="password" autoComplete="new-password"
                  placeholder="At least 10 characters"
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <p className="field-hint">
                  10 characters or more, with an uppercase letter, a lowercase letter and a number.
                </p>
              </div>

              <button type="submit" className="login-submit" disabled={loading}>
                {loading ? 'Creating your account…' : 'Create account'}
              </button>
            </form>

            <p className="login-footer-text">
              Already have an account?{' '}
              <button type="button" className="login-link" onClick={() => go('signIn')}>
                Sign in
              </button>
            </p>
          </>
        )}

        {view === 'confirm' && (
          <>
            <form className="login-form" onSubmit={onConfirm}>
              {!email && (
                <div className="field">
                  <label htmlFor="confirm-email">Email</label>
                  <input
                    id="confirm-email" type="email" autoComplete="email"
                    value={email} onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
              )}

              <div className="field">
                <label htmlFor="code">Confirmation code</label>
                <input
                  id="code" className="code-input"
                  type="text" inputMode="numeric" autoComplete="one-time-code"
                  placeholder="000000" maxLength={6}
                  value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  required
                />
              </div>

              <button type="submit" className="login-submit" disabled={loading || code.length < 6}>
                {loading ? 'Confirming…' : 'Confirm account'}
              </button>
            </form>

            <p className="login-footer-text">
              <button type="button" className="login-link" onClick={onResend} disabled={!email}>
                Send another code
              </button>
            </p>
            <p className="login-footer-text">
              <button type="button" className="login-link" onClick={() => go('signIn')}>
                Back to sign in
              </button>
            </p>
          </>
        )}

        {view === 'forgot' && (
          <>
            <form className="login-form" onSubmit={onForgot}>
              <div className="field">
                <label htmlFor="forgot-email">Email</label>
                <input
                  id="forgot-email" type="email" autoComplete="email"
                  placeholder="you@example.com"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              <button type="submit" className="login-submit" disabled={loading}>
                {loading ? 'Sending…' : 'Send reset code'}
              </button>
            </form>

            <p className="login-footer-text">
              <button type="button" className="login-link" onClick={() => go('signIn')}>
                Back to sign in
              </button>
            </p>
          </>
        )}

        {view === 'reset' && (
          <>
            <form className="login-form" onSubmit={onReset}>
              <div className="field">
                <label htmlFor="reset-code">Reset code</label>
                <input
                  id="reset-code" className="code-input"
                  type="text" inputMode="numeric" autoComplete="one-time-code"
                  placeholder="000000" maxLength={6}
                  value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  required
                />
              </div>

              <div className="field">
                <label htmlFor="reset-password">New password</label>
                <input
                  id="reset-password" type="password" autoComplete="new-password"
                  placeholder="At least 10 characters"
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>

              <button type="submit" className="login-submit" disabled={loading || code.length < 6}>
                {loading ? 'Changing…' : 'Change password'}
              </button>
            </form>

            <p className="login-footer-text">
              <button type="button" className="login-link" onClick={() => go('forgot', { keepEmail: true })}>
                Send another code
              </button>
            </p>
          </>
        )}

        {view === 'signIn' && process.env.NODE_ENV !== 'production' && (
          /* Development only, and stripped from a production build. The local
           * auth provider is the only thing that accepts these, and the server
           * refuses to start with it when NODE_ENV=production. */
          <p className="login-hint">
            Dev sign-in: <code>admin</code> or <code>atlas</code> / <code>password</code>
          </p>
        )}

        {view === 'newPassword' && (
          <form className="login-form" onSubmit={onNewPassword}>
            <div className="field">
              <label htmlFor="chosen-password">New password</label>
              <input
                id="chosen-password" type="password" autoComplete="new-password"
                placeholder="At least 10 characters"
                value={password} onChange={(e) => setPassword(e.target.value)}
                required
              />
              <p className="field-hint">
                10 characters or more, with an uppercase letter, a lowercase letter and a number.
              </p>
            </div>

            <button type="submit" className="login-submit" disabled={loading}>
              {loading ? 'Saving…' : 'Save and sign in'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
